/**
 * HeadingDragHandles - a hoverable :: grip on host-selected headings that drag-moves the WHOLE
 * section (the heading plus its body, until the next heading of the same or higher level) to
 * another position, in ONE ProseMirror transaction - so LockHeadings' before/after count check
 * still passes for a locked heading that merely moved.
 *
 * The host controls which headings get a grip (and double as drop anchors) via `getEnabled`,
 * a predicate over the heading's `dataKey` global attribute (see headingKeys.ts) + level -
 * read live through a ref like every other RichTextEditor host callback. Dragging uses native
 * HTML5 drag (the repo convention - no dnd library); the payload is the heading's dataKey, so
 * the source is re-located by key at drop time (positions shift, keys do not). Persistence is
 * the HOST's job: the Scope tab harvests the doc's item order on save.
 */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
const dragKey = new PluginKey('headingDragHandles');
const MIME = 'application/x-protoss-heading-key';
/** 2x3 dot grip, drawn inline so the DOM widget needs no React/icon dependency. */
const GRIP_SVG = '<svg width="10" height="16" viewBox="0 0 10 16" aria-hidden="true"><g fill="currentColor">' +
    '<circle cx="2.5" cy="3" r="1.4"/><circle cx="7.5" cy="3" r="1.4"/>' +
    '<circle cx="2.5" cy="8" r="1.4"/><circle cx="7.5" cy="8" r="1.4"/>' +
    '<circle cx="2.5" cy="13" r="1.4"/><circle cx="7.5" cy="13" r="1.4"/></g></svg>';
function headingDataKey(node) {
    const raw = node.attrs.dataKey;
    return typeof raw === 'string' && raw ? raw : null;
}
/** Find the enabled heading carrying `dataKey`, with its position. */
function findHeading(state, dataKey) {
    let hit = null;
    state.doc.descendants((node, pos) => {
        if (hit)
            return false;
        if (node.type.name === 'heading' && headingDataKey(node) === dataKey) {
            hit = { pos, node };
            return false;
        }
        return true;
    });
    return hit;
}
/** End of the section starting at `pos`: just before the next heading of level <= this one. */
function sectionEnd(state, pos, node) {
    const level = Number(node.attrs.level ?? 1);
    let end = state.doc.content.size;
    let found = false;
    state.doc.descendants((n, p) => {
        if (found)
            return false;
        if (p <= pos)
            return true;
        if (n.type.name === 'heading' && Number(n.attrs.level ?? 1) <= level) {
            end = p;
            found = true;
            return false;
        }
        return true;
    });
    return end;
}
/** Screen rect of the heading DOM node at `pos` - for midpoint (above/below) drop decisions. */
function headingRect(view, pos) {
    const dom = view.nodeDOM(pos);
    if (dom instanceof HTMLElement) {
        const r = dom.getBoundingClientRect();
        return { top: r.top, height: r.height };
    }
    return null;
}
/**
 * The container (functional-area section) a drop at `clientY` belongs to: the LAST section whose
 * heading MIDPOINT is at/above the cursor. So a section owns everything from its heading's lower half
 * down to the NEXT section's heading midpoint - which means the gap below a section's last item AND
 * the next heading's upper half both count as "the end of THIS section", a generous append zone near
 * the boundary (the previous top-of-next-heading boundary left the cursor snapping to the next H1).
 * Resolving by Y (not `posAtCoords`, which jumps to the nearest text near a collapsed boundary) is
 * what lets a drop reach a section's end. null when above the first container's midpoint.
 */
export function containerByClientY(view, clientY, containers) {
    let result = null;
    view.state.doc.descendants((node, pos) => {
        if (node.type.name !== 'heading')
            return true;
        const level = Number(node.attrs.level ?? 1);
        if (!containers(headingDataKey(node), level))
            return true;
        const rect = headingRect(view, pos);
        if (rect && clientY >= rect.top + rect.height / 2)
            result = { pos, node };
        return true;
    });
    return result;
}
/**
 * Resolve the insert position for an ITEM drop by the cursor's Y: the container section is the one
 * whose vertical span holds the cursor. Then:
 *  - over the section's own HEADING -> append to the END of the section (like the Prioritization
 *    pane's "drop on a group header appends to that group");
 *  - over the items -> before the first item heading whose vertical MIDPOINT is below the cursor;
 *    below the last item -> the section's end (a large "end of section" zone even when collapsed).
 * null = above the first section (rejected). Without a container predicate (legacy): midpoint over
 * all enabled headings, else after the last section.
 */
export function dropTarget(view, clientY, enabled, containers) {
    const state = view.state;
    if (containers) {
        const c = containerByClientY(view, clientY, containers);
        if (!c)
            return null;
        const end = sectionEnd(state, c.pos, c.node);
        // Cursor over the section heading itself -> append to the end of the section.
        const headRect = headingRect(view, c.pos);
        if (headRect && clientY < headRect.top + headRect.height)
            return end;
        let target = end;
        let found = false;
        state.doc.descendants((node, pos) => {
            if (found || node.type.name !== 'heading')
                return !found;
            if (pos <= c.pos || pos >= end)
                return true;
            const level = Number(node.attrs.level ?? 1);
            if (!enabled(headingDataKey(node), level))
                return true;
            const rect = headingRect(view, pos);
            if (rect && clientY < rect.top + rect.height / 2) {
                target = pos;
                found = true;
                return false;
            }
            return true;
        });
        return target;
    }
    let target = null;
    let lastPos = null;
    let lastNode = null;
    state.doc.descendants((node, pos) => {
        if (node.type.name !== 'heading')
            return true;
        const level = Number(node.attrs.level ?? 1);
        if (!enabled(headingDataKey(node), level))
            return true;
        lastPos = pos;
        lastNode = node;
        if (target === null) {
            const rect = headingRect(view, pos);
            if (rect && clientY < rect.top + rect.height / 2)
                target = pos;
        }
        return true;
    });
    if (target !== null)
        return target;
    if (lastPos !== null && lastNode !== null)
        return sectionEnd(state, lastPos, lastNode);
    return null;
}
/**
 * Resolve the insert position when the DRAGGED source is itself a container heading (a functional-
 * area H1): reorder it AMONG the other container headings by their vertical midpoints - so a section
 * can be dropped AFTER the last section, not only before one. (Item drops reparent INTO a container;
 * this reorders containers.)
 */
export function dropTargetAmongContainers(view, clientY, containers) {
    const state = view.state;
    let target = state.doc.content.size;
    let found = false;
    state.doc.descendants((node, pos) => {
        if (found || node.type.name !== 'heading')
            return !found;
        const level = Number(node.attrs.level ?? 1);
        if (!containers(headingDataKey(node), level))
            return true;
        const rect = headingRect(view, pos);
        if (rect && clientY < rect.top + rect.height / 2) {
            target = pos;
            found = true;
            return false;
        }
        return true;
    });
    return target;
}
/** A thin horizontal insert indicator (the drop line). */
function makeDropLine() {
    const el = document.createElement('div');
    el.className = 'rte-drop-line';
    el.setAttribute('contenteditable', 'false');
    return el;
}
/**
 * Feedback decorations for an in-flight drag: source highlight, drop line, target-area tint. EVERY
 * tracked position is guarded against the current doc - a stale dropPos/areaFrom/areaTo (positions
 * from before a mid-drag doc change) that lands out of range, or a target range that no longer bounds
 * a node exactly, would throw in DecorationSet.create and crash the whole editor render (which then
 * loops, since decorations rebuild every frame). forEach offsets are always valid, so only the
 * tracked values need guarding. Pure (doc + drag in, decorations out) so it is unit-testable.
 */
export function dragDecorations(doc, drag) {
    const decos = [];
    const size = doc.content.size;
    doc.forEach((node, offset) => {
        if (offset >= drag.from && offset < drag.to) {
            decos.push(Decoration.node(offset, offset + node.nodeSize, { class: 'rte-drag-source' }));
        }
    });
    if (drag.dropPos != null && drag.dropPos >= 0 && drag.dropPos <= size) {
        decos.push(Decoration.widget(drag.dropPos, makeDropLine, { side: -1, key: 'rte-drop-line' }));
        if (drag.areaFrom != null &&
            drag.areaTo != null &&
            drag.areaFrom >= 0 &&
            drag.areaTo <= size &&
            drag.areaFrom < drag.areaTo) {
            const areaNode = doc.nodeAt(drag.areaFrom);
            if (areaNode && drag.areaFrom + areaNode.nodeSize === drag.areaTo) {
                decos.push(Decoration.node(drag.areaFrom, drag.areaTo, { class: 'rte-drag-target-area' }));
            }
        }
    }
    return decos;
}
function makeHandle(view, dataKey, getContainers) {
    const el = document.createElement('span');
    el.className = 'rte-drag-handle';
    el.setAttribute('contenteditable', 'false');
    el.setAttribute('draggable', 'true');
    el.setAttribute('aria-label', 'Drag to reorder');
    el.setAttribute('data-rte-tooltip', 'Drag to reorder');
    el.innerHTML = GRIP_SVG;
    el.addEventListener('dragstart', (e) => {
        e.stopPropagation();
        if (e.dataTransfer) {
            e.dataTransfer.setData(MIME, dataKey);
            e.dataTransfer.effectAllowed = 'move';
            // Align the drag ghost to the cursor. The browser's default drag image is the whole grip (+ its
            // tooltip) captured well ABOVE the pointer, so the user aims by a ghost that floats ~a section
            // too high while the drop line (the true target) tracks the real cursor - they look mismatched.
            // Use a small standalone grip centered on the pointer. It is appended to <body>, NOT the
            // ProseMirror-managed grip, so the state dispatch below can't re-render it out from under the
            // browser's capture (pointing setDragImage at the LIVE grip is exactly what killed the drag).
            const ghost = document.createElement('div');
            ghost.className = 'rte-drag-ghost';
            ghost.innerHTML = GRIP_SVG;
            document.body.appendChild(ghost);
            e.dataTransfer.setDragImage(ghost, 5, 8);
            window.setTimeout(() => ghost.remove(), 0);
        }
        // Publish the drag source so the plugin can highlight the dragged range + show a drop line.
        const src = findHeading(view.state, dataKey);
        if (src) {
            const containers = getContainers();
            const level = Number(src.node.attrs.level ?? 1);
            const drag = {
                key: dataKey,
                from: src.pos,
                to: sectionEnd(view.state, src.pos, src.node),
                isContainer: !!containers && containers(dataKey, level),
                dropPos: null,
                areaFrom: null,
                areaTo: null,
            };
            view.dispatch(view.state.tr.setMeta(dragKey, drag));
        }
    });
    // Keep ProseMirror from turning the grab into a text selection (click still allowed
    // to start the native drag, so no preventDefault here).
    el.addEventListener('mousedown', (e) => e.stopPropagation());
    return el;
}
function buildDecorations(state, enabled, getContainers) {
    const decos = [];
    if (enabled) {
        state.doc.descendants((node, pos) => {
            if (node.type.name !== 'heading')
                return true;
            const key = headingDataKey(node);
            const level = Number(node.attrs.level ?? 1);
            if (!key || !enabled(key, level))
                return true;
            decos.push(Decoration.widget(pos + 1, (view) => makeHandle(view, key, getContainers), {
                side: -1,
                key: `dh-${key}`,
            }));
            return true;
        });
    }
    // Drag feedback: highlight the dragged range, draw the drop line, and (for an ITEM landing in a
    // section) highlight the destination functional area.
    const drag = dragKey.getState(state);
    if (drag)
        decos.push(...dragDecorations(state.doc, drag));
    return DecorationSet.create(state.doc, decos);
}
function handleDrop(view, e, enabled, containers) {
    if (!enabled)
        return false;
    // Identify the source from the plugin's tracked drag state FIRST, falling back to the drag payload.
    // In an editable view ProseMirror runs its OWN dragstart and can clearData()/overwrite the
    // dataTransfer, so getData(MIME) can come back empty at drop even though the drag was ours - which
    // silently no-ops the move. The plugin state carries the source key and is still set at drop time.
    const drag = dragKey.getState(view.state);
    const dataKey = drag?.key || e.dataTransfer?.getData(MIME);
    if (!dataKey)
        return false;
    e.preventDefault();
    const state = view.state;
    const src = findHeading(state, dataKey);
    if (!src)
        return true;
    const srcEnd = sectionEnd(state, src.pos, src.node);
    // Source-aware drop: dragging a CONTAINER heading (a section) reorders it among the other
    // containers; dragging anything else uses the container-gated item drop (reparent into a section).
    // Everything resolves from the cursor's Y (see dropTarget / containerByClientY).
    const srcLevel = Number(src.node.attrs.level ?? 1);
    const srcIsContainer = !!containers && containers(dataKey, srcLevel);
    const target = srcIsContainer && containers
        ? dropTargetAmongContainers(view, e.clientY, containers)
        : dropTarget(view, e.clientY, enabled, containers);
    // No valid container/target, or dropping inside the dragged range: nothing to move.
    if (target === null || (target >= src.pos && target <= srcEnd))
        return true;
    const slice = state.doc.slice(src.pos, srcEnd);
    const tr = state.tr.delete(src.pos, srcEnd);
    const mapped = tr.mapping.map(target, -1);
    tr.insert(mapped, slice.content);
    // Clear the drag state in the SAME transaction as the move: otherwise the state's dropPos/areaFrom/
    // areaTo (positions from the pre-move doc) are re-mapped and re-decorated against the post-move doc
    // for one render before the separate 'clear' lands - and a bad position there crashes the editor
    // (and strands the drag state, so it never recovers). Clearing atomically means the post-move state
    // has no drag decorations at all.
    tr.setMeta(dragKey, 'clear');
    view.dispatch(tr.scrollIntoView());
    return true;
}
export const HeadingDragHandles = Extension.create({
    name: 'headingDragHandles',
    addOptions() {
        return { getEnabled: () => null, getContainers: () => null };
    },
    addProseMirrorPlugins() {
        const getEnabled = () => this.options.getEnabled();
        const getContainers = () => this.options.getContainers?.() ?? null;
        return [
            new Plugin({
                key: dragKey,
                // Track the in-flight drag so the decorations can render the source highlight + drop line.
                // A meta of a DragState sets it, 'clear' resets, and plain doc changes remap the positions.
                state: {
                    init: () => null,
                    apply(tr, prev) {
                        const meta = tr.getMeta(dragKey);
                        if (meta === 'clear')
                            return null;
                        if (meta)
                            return meta;
                        if (prev && tr.docChanged) {
                            return {
                                ...prev,
                                from: tr.mapping.map(prev.from),
                                to: tr.mapping.map(prev.to),
                                dropPos: prev.dropPos != null ? tr.mapping.map(prev.dropPos) : null,
                                areaFrom: prev.areaFrom != null ? tr.mapping.map(prev.areaFrom) : null,
                                areaTo: prev.areaTo != null ? tr.mapping.map(prev.areaTo) : null,
                            };
                        }
                        return prev;
                    },
                },
                props: {
                    decorations: (state) => buildDecorations(state, getEnabled(), getContainers),
                    handleDOMEvents: {
                        dragover: (view, e) => {
                            const enabled = getEnabled();
                            if (!enabled || !e.dataTransfer?.types.includes(MIME))
                                return false;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            // Recompute the live drop position (same source-aware rules as the actual drop) so the
                            // drop line + target-area highlight track the cursor. Null while over the dragged range.
                            const drag = dragKey.getState(view.state);
                            if (drag) {
                                const containers = getContainers();
                                let dropPos = null;
                                let areaFrom = null;
                                let areaTo = null;
                                const t = drag.isContainer && containers
                                    ? dropTargetAmongContainers(view, e.clientY, containers)
                                    : dropTarget(view, e.clientY, enabled, containers);
                                if (t !== null && !(t >= drag.from && t <= drag.to)) {
                                    dropPos = t;
                                    // For an item drop, remember the destination functional-area heading to highlight.
                                    if (!drag.isContainer && containers) {
                                        const c = containerByClientY(view, e.clientY, containers);
                                        if (c) {
                                            areaFrom = c.pos;
                                            areaTo = c.pos + c.node.nodeSize;
                                        }
                                    }
                                }
                                if (dropPos !== drag.dropPos ||
                                    areaFrom !== drag.areaFrom ||
                                    areaTo !== drag.areaTo) {
                                    view.dispatch(view.state.tr.setMeta(dragKey, { ...drag, dropPos, areaFrom, areaTo }));
                                }
                            }
                            return true;
                        },
                        dragend: (view) => {
                            if (dragKey.getState(view.state)) {
                                view.dispatch(view.state.tr.setMeta(dragKey, 'clear'));
                            }
                            return false;
                        },
                        drop: (view, e) => {
                            const handled = handleDrop(view, e, getEnabled(), getContainers());
                            if (dragKey.getState(view.state)) {
                                view.dispatch(view.state.tr.setMeta(dragKey, 'clear'));
                            }
                            return handled;
                        },
                    },
                },
            }),
        ];
    },
});
//# sourceMappingURL=headingDragHandles.js.map